import AttackToolsChart from '@components/AttackToolsChart';
import { CarouselItem } from '@components/CarouselItem';
import Event from '@components/Event';
import EventModal from '@components/EventModal';
import Layout from '@components/Layout';
import MainCTA from '@components/MainCTA';
import Modal from '@components/Modal';
import PersonModal from '@components/PersonModal';
import Profile from '@components/Profile';
import Ticker from '@components/Ticker';
import { SectionHeading, Text } from '@components/Typography';
import { Box, CarouselButton, CarouselNav, Grid, ScrollCarousel } from '@fastly/consistently';
import Carousel from '@components/Carousel';
import { EventModeContext, EventModes, useEventMode } from '@hooks/use-event-mode';
import { graphql, PageProps } from 'gatsby';
import * as React from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { Microcopy, PageCopy } from 'types/copy';

// type IndexPageProps = PageProps<>;
type IndexPageProps = {
  data: GatsbyTypes.IndexPageQuery;
} & PageProps;

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
`;

const HeadingContainerVault = styled(HeadingContainer)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;

  @media (min-width: 1900px) {
    flex-direction: row;
    align-items: center;
    > h3 {
      white-space: nowrap;
    }
  }
`;

const Main = styled.main`
  padding: 0 32px 32px;
  display: grid;
  gap: 32px;
  grid-template-areas:
    'registration'
    'schedule'
    'guests'
    'hosts'
    'chart'
    'vault';

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-areas:
      'registration schedule'
      'guests schedule'
      'hosts schedule'
      'chart vault';
    grid-template-columns: repeat(2, 1fr);
    gap: 24px 32px;
    padding: 0 32px 32px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 50px 88px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    grid-template-areas:
      'registration schedule guests'
      'chart schedule hosts'
      'chart schedule vault';
    grid-template-columns: repeat(3, 1fr);
    gap: 32px 50px;
    min-height: calc(100vh - 268px); // stick the ticker to the bottom of viewport
  }
`;

const Registration = styled(MainCTA)`
  grid-area: registration;
`;
const Chart = styled.section`
  grid-area: chart;
`;
const Schedule = styled.section`
  grid-area: schedule;
`;
const Guests = styled.section`
  grid-area: guests;
`;
const Hosts = styled.section`
  grid-area: hosts;
`;
const KnowledgeVault = styled.section`
  grid-area: vault;
`;

const IndexPage: React.FC<IndexPageProps> = ({
  data: { sessions, guests, hosts, knowledgeVault, pageCopy: pageCopyRaw, beforeEvent, liveEvent, afterEvent, ogImage },
}): JSX.Element => {
  const pageCopy = pageCopyRaw as PageCopy;

  const [eventMode, setEventMode] = React.useState(useEventMode(sessions));
  const [modalContent, setModalContent] = React.useState<React.ReactNode | null>(null);
  const handleModal = (
    content?: object,
    type?: 'event' | 'person',
    sessionMode: string | undefined = eventMode.mode
  ) => {
    const copy: GatsbyTypes.MicrocopyFragment['registration']['button_text'] = {
      [EventModes.Before]: beforeEvent?.registration?.button_text,
      [EventModes.Live]: liveEvent?.registration?.button_text,
      [EventModes.PreRoll]: liveEvent?.registration?.button_text,
      [EventModes.After]: afterEvent?.registration?.button_text,
    };

    if (type === 'event') {
      return setModalContent((prevState) =>
        !prevState && content ? <EventModal {...content} mode={sessionMode} copy={copy} /> : null
      );
    }

    return setModalContent((prevState) =>
      !prevState && content ? <PersonModal {...content} mode={sessionMode} copy={copy} /> : null
    );
  };

  let timedContent = afterEvent as Microcopy;

  switch (eventMode.mode) {
    case EventModes.Live:
      if (liveEvent) timedContent = liveEvent as Microcopy;
      break;
    case EventModes.PreRoll:
      if (liveEvent) timedContent = liveEvent as Microcopy;
      break;
    case EventModes.Before:
      if (beforeEvent) timedContent = beforeEvent as Microcopy;
      break;
    case EventModes.After:
      if (afterEvent) timedContent = afterEvent as Microcopy;
  }
  return (
    <EventModeContext.Provider value={[eventMode, setEventMode]}>
      <Layout>
        <Helmet>
          <title>{beforeEvent.registration.headline}</title>
          <meta property="og:title" content={beforeEvent.registration.headline} />
          <meta property="og:description" content={beforeEvent.registration.subcopy} />
          <meta name="description" content={beforeEvent.registration.subcopy} />
          <meta property="og:image" content={ogImage.original.src} />
          <link rel="canonical" href="https://www.fastly.com/deptofknow/" />
        </Helmet>
        <Modal
          isOpen={modalContent ? true : false}
          content={modalContent && modalContent}
          handleModal={handleModal}
        ></Modal>
        <Main>
          {/* Registration */}
          <Registration copy={timedContent.registration} />

          <Chart>
            <Box mb={24}>
              <SectionHeading>{pageCopy.chart.headline}</SectionHeading>
            </Box>
            <Box mb={16}>
              <Text as="p" size="sm" variant="muted">
                {pageCopy?.chart?.subcopy}
              </Text>
            </Box>
            <AttackToolsChart />
          </Chart>

          {/* Schedule */}
          <Schedule>
            <Grid>
              <SectionHeading>{timedContent.schedule.headline}</SectionHeading>
              <Grid as="ul" gridGap={{ _: '32px', xxl: '48px' }}>
                {sessions.nodes.map((session: GatsbyTypes.SessionFragment) => {
                  return (
                    <li key={session.date}>
                      <Event session={session} onGuestClick={handleModal} />
                    </li>
                  );
                })}
              </Grid>
            </Grid>
          </Schedule>

          {/* People */}
          <Guests>
            <Grid gap="18px">
              <HeadingContainer as="h3">
                <SectionHeading as="span">{pageCopy.guests}</SectionHeading>
                <Grid gap={0}>
                  <Text size="sm" weight="bold">
                    {pageCopy.tagline}
                  </Text>
                  <Text size="sm" weight="bold">
                    {pageCopy.tagline_two}
                  </Text>
                </Grid>
              </HeadingContainer>

              <Grid as="ul" cols={3} gap="24px">
                {guests.nodes.map((person) => (
                  <li key={person.id}>
                    <Profile person={person} variant="guest" handleModal={handleModal} />
                  </li>
                ))}
              </Grid>
            </Grid>
          </Guests>

          <Hosts>
            <Grid gap="18px">
              <SectionHeading>{pageCopy.hosts}</SectionHeading>
              <Grid as="ul" cols={3} gap="24px">
                {hosts.nodes.map((person) => (
                  <li key={person.id}>
                    <Profile person={person} variant="host" handleModal={handleModal} />
                  </li>
                ))}
              </Grid>
            </Grid>
          </Hosts>

          {/* knowledge Vault */}
          <KnowledgeVault>
            <Grid gap="0">
              <HeadingContainerVault>
                <SectionHeading>{pageCopy.vault.headline}</SectionHeading>
                <Text size="sm">{pageCopy.vault.subcopy}</Text>
              </HeadingContainerVault>
              <Carousel
                itemWidth={{
                  sm: '190px',
                  md: '200px',
                  lg: '233px',
                  xl: '166px',
                }}
                gap={{ sm: '25px', md: '0px', lg: '0px', xl: '0px' }}
                rows={{ sm: 1, md: 2, lg: 2, xl: 2 }}
                navPosition="side"
                navMargin="36px"
                renderNavComponent={(props: any) => (
                  <CarouselNav {...props.nav}>
                    <CarouselButton
                      type="chevronWide"
                      variant="solid"
                      color="#97A1A6"
                      colorHover="#04B2E1"
                      width="19px"
                      height="55px"
                      {...props.previous}
                    />
                    <CarouselButton
                      type="chevronWide"
                      variant="solid"
                      color="#97A1A6"
                      colorHover="#04B2E1"
                      width="19px"
                      height="55px"
                      {...props.next}
                    />
                  </CarouselNav>
                )}
              >
                {knowledgeVault.nodes.map((content) => (
                  <CarouselItem data={content} key={content.title} />
                ))}
              </Carousel>
            </Grid>
          </KnowledgeVault>
        </Main>
        <Ticker />
      </Layout>
    </EventModeContext.Provider>
  );
};

export default IndexPage;

export const query = graphql`
  fragment Person on PeopleJson {
    id
    company
    name
    role
    title
    bio
    session {
      id
      date
      brandLiveUrl
    }
    social {
      linkedin
      twitter
      website
    }
    image {
      childImageSharp {
        gatsbyImageData(width: 200, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
      }
    }
  }

  fragment Microcopy on MicrocopyJson {
    registration {
      button_text
      headline
      subcopy
    }
    schedule {
      headline
    }
  }

  fragment Session on SessionsJson {
    id
    date
    description
    guest {
      name
    }
    title
    brandLiveUrl
    duration
    iconName
    image {
      relativePath
    }
  }

  query IndexPage {
    sessions: allSessionsJson(sort: { fields: date, order: ASC }) {
      nodes {
        ...Session
      }
    }
    guests: allPeopleJson(filter: { role: { eq: "guest" } }, sort: { fields: order, order: ASC }) {
      nodes {
        ...Person
      }
    }
    hosts: allPeopleJson(filter: { role: { eq: "host" } }, sort: { fields: order, order: ASC }) {
      nodes {
        ...Person
      }
    }
    knowledgeVault: allKnowledgeVaultJson {
      nodes {
        title
        url
        author
        type
      }
    }
    pageCopy: staticcopyJson {
      chart {
        headline
        subcopy
      }
      guests
      hosts
      tagline
      tagline_two
      vault {
        headline
        subcopy
      }
    }
    beforeEvent: microcopyJson(mode: { eq: "before" }) {
      ...Microcopy
    }
    liveEvent: microcopyJson(mode: { eq: "live" }) {
      ...Microcopy
    }
    afterEvent: microcopyJson(mode: { eq: "after" }) {
      ...Microcopy
    }
    ogImage: imageSharp(original: { src: { regex: "/dok-og-image/" } }) {
      original {
        src
      }
    }
  }
`;
